import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import ApexCharts from 'apexcharts';
import {
  ApexChart,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTooltip,
  ChartComponent,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { finalize } from 'rxjs';
import { TabelaPaginacaoComponent } from 'src/app/shared/components/tabela-paginacao/tabela-paginacao.component';
import { environment } from 'src/environments/environment';
import { Column } from '../../interface/colunas-tabelas.interface';
import {
  PaginationMetricas,
  TabelasObrasFase,
} from '../../interface/tabela-obras-fase.interface';
import { DropdownService } from '../../service/dropdown-metricas.service';
import { MetricasService } from '../../service/metricas.service';
import { FiltroPeriodoComponent } from '../filtros-metricas/filtro-periodo/filtro-periodo.component';
import { PeriodoService } from '../filtros-metricas/filtro-periodo/service/filtro-periodo.service';
import { FiltroResponsavelComponent } from '../filtros-metricas/filtro-responsavel/filtro-responsavel.component';
import { FiltroTipoObraComponent } from '../filtros-metricas/filtro-tipo-obra/filtro-tipo-obra.component';
import { MOTIVO_PERDA_COLUMNS } from './motivo-perda.columns';
import { ModalAguardandoDownloadComponent } from 'src/app/shared/components/modal-aguardando-download/modal-aguardando-download.component';

(window as any).ApexCharts = ApexCharts;

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  responsive: ApexResponsive[];
  colors: string[];
  plotOptions: ApexPlotOptions;
  tooltip: ApexTooltip;
  legend?: any;
};

export interface LostReason {
  lostReasonID: number;
  lostReason: string;
  workQuantity: number;
}

@Component({
  selector: 'app-motivos-perda',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    FiltroPeriodoComponent,
    FiltroTipoObraComponent,
    FiltroResponsavelComponent,
    TabelaPaginacaoComponent,
    ModalAguardandoDownloadComponent,
  ],
  templateUrl: './motivos-perda.component.html',
  styleUrls: ['./motivos-perda.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MotivosPerdaComponent implements OnInit {
  processandoAcao: boolean = false;
  mensagemProcessando: string = 'Aguardando download...';
  @Input() selectedPeriodo: Date[] | null = null;
  @Input() selectedUsuario: string = 'Todos';
  @Input() obraIds: number[] = [];
  @Input() responsavel: any[] = [];
  @Output() periodoChange = new EventEmitter<{
    periodBegin: string;
    periodEnd: string;
  } | null>();

  @Output() rowClick = new EventEmitter<any>();
  @Output() obraSelecionada = new EventEmitter<{
    workId: number;
    obraIds: number[];
    moduleId?: number;
    workType?: string;
    workTypesPorObra?: Record<number, number>;
  }>();
  @Output() avancar = new EventEmitter<void>();
  @Output() voltarObra = new EventEmitter<void>();
 @Output() companyNameClick = new EventEmitter<{
    companyId?: number;
    companyName?: string;
    workType?: string;
  }>();
  @Output() atualizandoMetricasChange = new EventEmitter<boolean>();

  @ViewChild('chart') chart?: ChartComponent;

  BASE_IMG_URL: string = environment.BASE_IMG_URL;

  columns: Column[] = MOTIVO_PERDA_COLUMNS;
  arrMotivos: LostReason[] = [];
  todosOsMotivos: LostReason[] = [];
  tableData: TabelasObrasFase[] = [];

  config: Partial<BsDaterangepickerConfig> = {
    containerClass: 'bs-datepicker',
    rangeInputFormat: 'DD/MM/YYYY',
    showWeekNumbers: false,
    selectFromOtherMonth: true,
    isAnimated: true,
    displayMonths: 1,
  };
  pagination: PaginationMetricas = {
    pageNext: 2,
    pagePrev: 0,
    recordsCount: 0,
    pageFirst: 1,
    pageLast: 1,
    pageActive: true,
    page: 1,
    pageSize: 10,
    shortcuts: 0,
    hasValue: false,
    shortcutPages: [],
    records: [],
  };
  chartOptions!: ChartOptions;

  currentPage: number = 1;
  pageNumber: number = 1;
  pageSize: number = 50;
  idObraSelecionada: number | null = null;
  indiceObraAtual: number = 0;

  exibirFichaDaObra: boolean = false;
  mostrarMotivos = false;
  chartReady = false;

  viewMode: 'chart' | 'table' = 'chart';

  filtroMotivosSelecionados: any[] = [];
  tiposSelecionados: any;

  get desabilitarExportar(): boolean {
    return this.arrMotivos.length === 0 && this.tableData.length === 0;
  }

  totalPagesValue: number = 0;

  trackByFn = (index: number, item: any) => item.id;

  private periodoService = inject(PeriodoService);

  private sincronizarPeriodoComFiltro = effect(() => {
    const periodo = this.periodoService.periodoSelecionado()?.();

    this.listReasons(periodo ?? undefined);
    this;
  });

  constructor(
    private _metricasService: MetricasService,
    private dropdownService: DropdownService
  ) {}

  ngOnInit(): void {
    this.chartOptions = {
      series: [],
      chart: {
        type: 'pie',
      },
      labels: [],
      responsive: [],
      colors: [],
      plotOptions: {},
      tooltip: {},
    };

    this.filterLostReasons();
    this.dropdownService.getActiveDropdown().subscribe((active) => {
      if (active !== 'motivos') {
        this.mostrarMotivos = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['responsavel'] && !changes['responsavel'].firstChange) {
      const responsaveis = changes['responsavel'].currentValue;

      if (
        Array.isArray(responsaveis) &&
        responsaveis.length > 0 &&
        responsaveis[0]?.userLevelId
      ) {
        const p = this.periodObjFromSelected();

        this.listReasonsTable(p);
        this.listReasons(p);
      } else {
      }
    }

    if (changes['selectedPeriodo'] && !changes['selectedPeriodo'].firstChange) {
      const periodo =
        this.selectedPeriodo?.length === 2
          ? {
              periodBegin: this.selectedPeriodo[0].toISOString().slice(0, 10),
              periodEnd: this.selectedPeriodo[1].toISOString().slice(0, 10),
            }
          : undefined;
      this.listReasonsTable(periodo);
      this.listReasons(periodo);
    }
  }

  onResponsaveisRecebidos(resps) {
    if (!Array.isArray(resps)) {
      console.error('Responsáveis recebidos não são um array:', resps);
      return;
    }
    if (resps.length === 0) {
      console.warn('Array de responsáveis está vazio.');
    }

    if (resps.length > 0 && resps[0].id === undefined) {
      console.error('Responsável não possui propriedade id:', resps[0]);
    }
  }

  listReasons(periodo?: { periodBegin: string; periodEnd: string }): void {
    this.atualizandoMetricasChange.emit(true);
    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      this.atualizandoMetricasChange.emit(false);
      return;
    }

    const motivosValidos = this.filtroMotivosSelecionados.filter(
      (m) => !isNaN(m) && m !== null && m !== undefined
    );
    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }
    const requestBody = {
      lostReasonID: motivosValidos.length ? motivosValidos.join(',') : '',
      categoryID: this.tiposSelecionados?.join(',') ?? '',
      responsibleID: responsibleID,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
    };
    this._metricasService
      .listWorkLostReason(requestBody)
      .pipe(finalize(() => this.atualizandoMetricasChange.emit(false)))
      .subscribe(
        (response) => {
          this.atualizandoMetricasChange.emit(false);
          if (response && response.data && response.data.length > 0) {
            this.arrMotivos = response.data;

            const series = response.data.map(
              (item: LostReason) => item.workQuantity
            );
            const labels = response.data.map(
              (item: LostReason) => item.lostReason
            );

            this.chartOptions = {
              series: series,
              chart: {
                type: 'pie',
                height: 260,
              },
              labels: labels,
              colors: ['#eb5e28', '#252422', '#6b6864', '#b4ada1', '#f2efe5'],
              plotOptions: {
                pie: {
                  donut: {
                    size: '40%',
                  },
                },
              },
              tooltip: {
                y: {
                  formatter: (val: number) => `${val} obras`,
                },
              },
              legend: {
                show: false,
                position: 'right',
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: 'Hiragino-W3", sans-serif',
                labels: {
                  colors: [
                    '#586572',
                    '#586572',
                    '#586572',
                    '#586572',
                    '#586572',
                  ],
                  useSeriesColors: false,
                },
                markers: {
                  radius: 12,
                  offsetX: -5,
                },
                itemMargin: {
                  vertical: 10,
                  horizontal: 20,
                },
                formatter: (seriesName: string, opts) => {
                  const val = opts.w.globals.series[opts.seriesIndex];
                  return `
                <div style=\"display: flex; align-items: center; justify-content: space-between;\">\n                <div>\n                  <span style=\"padding-right: 12px;\">${seriesName}</span>\n               \n                </div>\n                <div>\n                 <strong>${val}</strong>\n                </div>\n              `;
                },
              },
              responsive: [
                {
                  breakpoint: 768,
                  options: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                },
              ],
            };

            this.chartReady = true;
          } else {
            this.arrMotivos = [];
            this.chartReady = false;
          }
        },
        (error) => {
          this.chartReady = false;
          console.error('Erro ao buscar fases de obra:', error);
        }
      );
  }

  listReasonsTable(periodo?: { periodBegin: string; periodEnd: string }): void {
    this.atualizandoMetricasChange.emit(true);
    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      this.atualizandoMetricasChange.emit(false);
      return;
    }

    const motivosValidos = this.filtroMotivosSelecionados.filter(
      (m) => !isNaN(m) && m !== null && m !== undefined
    );
    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }
    const requestBody = {
      lostReasonID: motivosValidos.length ? motivosValidos.join(',') : '',
      categoryID: this.tiposSelecionados?.join(',') ?? '',
      responsibleID: responsibleID,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
      pagination: {
        page: this.currentPage,
        pageSize: this.pageSize,
        shortcuts: 10,
        ordenation: '',
      },
    };
    this._metricasService
      .listWorkLostReasonTable(requestBody)
      .pipe(finalize(() => this.atualizandoMetricasChange.emit(false)))
      .subscribe(
        (response) => {
          this.atualizandoMetricasChange.emit(false);
          if (response && response.data && response.data.records) {
            this.tableData = response.data.records.map((item: any) => ({
              responsibleId: item.responsibleId,
              responsibleName: item.responsibleName,
              stateId: item.stateId,
              stateName: ` ${item.cityName} - ${item.stateName} `,
              cityId: item.cityId,
              cityName: item.cityName,
              workId: item.workId,
              companyId: item.companyId,
              companyName: item.companyName,
              workName: item.workName,
              workTypeId: item.workTypeId,
              workType: item.workType,
              lastReasonID: item.lastReasonID,
              lastReason: item.lastReason,
              lastAction: item.lastAction,
              lastActionDate: formatDate(
                item.lastActionDate,
                'dd/MM/yyyy',
                'en-US'
              ),
              lastNote: item.lastNote,
              lastPotential: item.lastPotential,
              viewStatus: item.viewStatus,
              assignedDate: formatDate(
                item.assignedDate,
                'dd/MM/yyyy',
                'en-US'
              ),
            }));

            this.obraIds = this.tableData.map((obra) => obra.workId);

            const totalRegistros =
              response.data.totalRecords ?? response.data.recordsCount ?? 0;

            this.totalPagesValue = Math.ceil(totalRegistros / this.pageSize);

            this.pagination = {
              ...this.pagination,
              page: this.currentPage,
              recordsCount: totalRegistros,
              pageFirst: 1,
              pageLast: this.totalPagesValue,
              pageNext:
                this.currentPage < this.totalPagesValue
                  ? this.currentPage + 1
                  : null,
              pagePrev: this.currentPage > 1 ? this.currentPage - 1 : null,
              hasValue: this.tableData.length > 0,
              records: this.tableData,
            };
          } else {
            console.error('response.data.records não está definido:', response);
            this.tableData = [];
          }
        },
        (error) => {
          this.atualizandoMetricasChange.emit(false);
          console.error('Erro ao buscar fases de obra:', error);
        }
      );
  }

  listResponsaveis(): void {
    if (this.responsavel.length === 0) {
      this._metricasService.listResponsible().subscribe(
        (response) => {
          this.responsavel = response.data.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        },
        (error) => {
          console.error('Erro ao buscar responsáveis:', error);
        }
      );
    }
  }

  mudarPagina(novaPagina: number): void {
    this.currentPage = novaPagina;
    this.listReasonsTable();
  }

  get totalPages(): number {
    return this.totalPagesValue;
  }

  onPageChange(page: number): void {
    this.pagination.page = page;
    this.listReasonsTable();
  }

  toggleMotivos() {
    const isOpening = !this.mostrarMotivos;
    this.dropdownService.setActiveDropdown(isOpening ? 'motivos' : null);
    this.mostrarMotivos = isOpening;
  }

  private parseYmdToLocal(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  onPeriodoSelecionadoChange(
    periodo: { periodBegin: string; periodEnd: string } | null
  ) {
    if (periodo && periodo.periodBegin && periodo.periodEnd) {
      this.selectedPeriodo = [
        this.parseYmdToLocal(periodo.periodBegin),
        this.parseYmdToLocal(periodo.periodEnd),
      ];
    } else {
      this.selectedPeriodo = null;
    }
    this.listReasonsTable(periodo ?? undefined);
    this.listReasons(periodo ?? undefined);
  }

  private periodObjFromSelected():
    | { periodBegin: string; periodEnd: string }
    | undefined {
    if (this.selectedPeriodo?.length === 2) {
      return {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    return undefined;
  }

  private formatYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDateToBackendFormat(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  toggleView(mode: 'chart' | 'table') {
    this.viewMode = mode;
  }

  exportExcel() {
    this.mensagemProcessando = 'Aguardando download do Excel...';
    this.processandoAcao = true;
    const filtros = this.capturarFiltrosAtuais();
    this._metricasService.downloadExcelLostReason(filtros).subscribe(
      (response) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'motivos-perda.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.processandoAcao = false;
      },
      (error) => {
        this.processandoAcao = false;
        console.error('Erro ao exportar Excel:', error);
      }
    );
  }

  capturarFiltrosAtuais(): any {
    const filtros: any = {};

    let period: { periodBegin: string; periodEnd: string } | null = null;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      period = {
        periodBegin: this.formatDateToBackendFormat(this.selectedPeriodo[0]),
        periodEnd: this.formatDateToBackendFormat(this.selectedPeriodo[1]),
      };
    }

    if (period) {
      filtros.period = period;
    }

    let responsibleId: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleId = Number(this.selectedUsuario) || 0;
    }

    if (responsibleId !== 0) {
      filtros.responsible = {
        responsible: responsibleId
      };
    }

    const motivosIds = this.filtroMotivosSelecionados || [];
    if (motivosIds.length > 0) {
      filtros.reason = {
        id: motivosIds[0]
      };
    }

    const tiposObraIds = this.tiposSelecionados || [];
    if (tiposObraIds.length > 0) {
      filtros.type = {
        ids: tiposObraIds
      };
    }

    return filtros;
  }

  onTiposSelecionadosChange(tipos: number[]): void {
    this.tiposSelecionados = tipos;
    const periodo = this.periodoService.periodoSelecionado()?.();
    this.listReasons(periodo ?? undefined);
    this.listReasonsTable(periodo ?? undefined);
  }

  limparMotivos(): void {
    this.filtroMotivosSelecionados = [];
    this.mostrarMotivos = false;
    this.listReasons();
    this.listReasonsTable();
  }

  selecionarMotivo(motivo: string) {
    this.filtroMotivosSelecionados.push(Number(motivo));
    this.listReasons();
    this.listReasonsTable();
  }
  onMotivoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;

    if (valor === 'Todos') {
      this.filtroMotivosSelecionados = [];
    } else {
      const motivoSelecionado = Number(valor);
      this.filtroMotivosSelecionados = [motivoSelecionado];
    }

    const periodo = {
      periodBegin: this.selectedPeriodo?.[0]?.toISOString() ?? '',
      periodEnd: this.selectedPeriodo?.[1]?.toISOString() ?? '',
    };

    this.listReasons(periodo);
    this.listReasonsTable(periodo);

    this.mostrarMotivos = false;
    this.atualizandoMetricasChange.emit(false);
  }

  onResponsavelIdChange(responsavelId: number): void {
    const periodo = this.periodObjFromSelected();
    this.listReasons(periodo);
    this.listReasonsTable(periodo);
  }

  onWorkNameSelected(workId: number): void {
    const obra = this.tableData.find((data) => data.workId === workId);
    const workTypesPorObra: Record<number, number> = {};
    this.tableData.forEach((data) => {
      if (data.workTypeId != null) workTypesPorObra[data.workId] = data.workTypeId;
    });
    this.obraSelecionada.emit({
      workId,
      obraIds: this.tableData.map((data) => data.workId),
      ...(obra?.workTypeId != null ? { moduleId: obra.workTypeId } : {}),
      ...(obra?.workType ? { workType: obra.workType } : {}),
      workTypesPorObra,
    });
  }

 onCompanyNameClick(row: any): void {
    this.companyNameClick.emit({
      companyId: row.companyId,
      companyName: row.companyName,
      workType: row.workType,
    });
  }

  filterLostReasons(): void {
    this._metricasService.listFilterLostReason().subscribe(
      (response) => {
        this.todosOsMotivos = response.data;
      },
      (error) => {
        console.error('Erro ao filtrar motivos de perda:', error);
      }
    );
  }
}
